import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback, useMemo } from 'react';
import { format } from 'date-fns';

// Define the log entry interface
export interface LogEntry {
    timestamp: string;
    message: string;
    deviceId?: number;
    deviceName?: string;
    level: 'info' | 'warning' | 'error';
    source: 'ui'; // Future expansion: 'device', 'network', etc.
    userId?: string;
    userName?: string;
}

// Define the context type
interface LoggingContextType {
    logs: LogEntry[];
    addLog: (log: Omit<LogEntry, 'timestamp'>) => void;
    clearLogs: () => void;
    getDeviceLogs: (deviceId: number) => LogEntry[];
    getFormattedLog: (log: LogEntry) => string;
}

// Create context
const LoggingContext = createContext<LoggingContextType | undefined>(undefined);

// Provider props
interface LoggingProviderProps {
    children: ReactNode;
}

// Hook for using the logging context
export const useLogging = (): LoggingContextType => {
    const context = useContext(LoggingContext);
    if (!context) {
        throw new Error('useLogging must be used within a LoggingProvider');
    }
    return context;
};

// Provider component
export const LoggingProvider: React.FC<LoggingProviderProps> = ({ children }) => {
    // State for logs
    const [logs, setLogs] = useState<LogEntry[]>(() => {
        // Load logs from local storage on initialization
        const savedLogs = localStorage.getItem('ui_logs');
        return savedLogs ? JSON.parse(savedLogs) : [];
    });

    // Save logs to local storage when they change (max 500 entries)
    useEffect(() => {
        try {
            const trimmed = logs.length > 500 ? logs.slice(0, 500) : logs;
            localStorage.setItem('ui_logs', JSON.stringify(trimmed));
        } catch {
            // Quota exceeded — clear old logs and retry
            try {
                const reduced = logs.slice(0, 100);
                localStorage.setItem('ui_logs', JSON.stringify(reduced));
            } catch {
                localStorage.removeItem('ui_logs');
            }
        }
    }, [logs]);

    // Add a new log
    const addLog = useCallback((log: Omit<LogEntry, 'timestamp'>) => {
        const newLog: LogEntry = {
            ...log,
            timestamp: new Date().toISOString(),
        };
        setLogs(prevLogs => {
            const updated = [newLog, ...prevLogs];
            return updated.length > 500 ? updated.slice(0, 500) : updated;
        });
    }, []);

    // Clear all logs
    const clearLogs = useCallback(() => {
        setLogs([]);
    }, []);

    // Get logs for a specific device
    const getDeviceLogs = useCallback((deviceId: number) => {
        return logs.filter(log => log.deviceId === deviceId);
    }, [logs]);

    // Format log message for display
    const getFormattedLog = useCallback((log: LogEntry): string => {
        const timestamp = format(new Date(log.timestamp), 'yyyy-MM-dd HH:mm:ss');
        const userName = log.userName || 'System';
        const deviceInfo = log.deviceName ? ` [${log.deviceName}]` : '';
        return `${timestamp} - ${userName}${deviceInfo}: ${log.message}`;
    }, []);

    // Intercept console logs for testing purposes
    useEffect(() => {
        // Save original console methods
        const originalConsoleLog = console.log;
        const originalConsoleWarn = console.warn;
        const originalConsoleError = console.error;

        // Override console.log
        console.log = (...args) => {
            originalConsoleLog(...args);
            if (typeof args[0] === 'string' && args[0].includes('Test')) {
                addLog({
                    message: args[0],
                    level: 'info',
                    source: 'ui',
                    userName: 'Test User'
                });
            }
        };

        // Override console.warn
        console.warn = (...args) => {
            originalConsoleWarn(...args);
            if (typeof args[0] === 'string' && args[0].includes('Test')) {
                addLog({
                    message: args[0],
                    level: 'warning',
                    source: 'ui',
                    userName: 'Test User'
                });
            }
        };

        // Override console.error
        console.error = (...args) => {
            originalConsoleError(...args);
            if (typeof args[0] === 'string' && args[0].includes('Test')) {
                addLog({
                    message: args[0],
                    level: 'error',
                    source: 'ui',
                    userName: 'Test User'
                });
            }
        };

        // Cleanup function to restore original console methods
        return () => {
            console.log = originalConsoleLog;
            console.warn = originalConsoleWarn;
            console.error = originalConsoleError;
        };
    }, [addLog]);

    // Provide the context
    const contextValue = useMemo(() => ({
        logs,
        addLog,
        clearLogs,
        getDeviceLogs,
        getFormattedLog
    }), [logs, addLog, clearLogs, getDeviceLogs, getFormattedLog]);

    return (
        <LoggingContext.Provider value={contextValue}>
            {children}
        </LoggingContext.Provider>
    );
};

export default LoggingProvider; 